import { match } from 'ts-pattern'

import { useForm } from '@sequencehq/utils'
import { isUrl } from '@sequencehq/utils/validation'
import { useNotifications } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'

import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

export type CustomerPortalSettings = Awaited<
  ReturnType<typeof dashboard20240730Client.getMerchantForSequenceAccount>
>['data']

export const useEndCustomerPortalSettings = (data?: CustomerPortalSettings) => {
  const { displayNotification } = useNotifications()

  const mutator = useMutation(dashboard20240730Client.putMerchant)

  const { fields, queries } = useForm({
    value: {
      customerPortalEnabled: data?.customerPortalEnabled,
      showUsageInCustomerPortal: data?.showUsageInCustomerPortal,
      showAppUrl: Boolean(data?.appUrl),
      appUrl: data?.appUrl,
      customerPortalAuthRequirement: match(data?.customerPortalAuthRequirement)
        .with('REQUIRED', () => true)
        .with('NOT_REQUIRED', undefined, () => false)
        .exhaustive()
    },
    showValidationErrors: true,
    fieldConfiguration: [
      {
        property: 'customerPortalEnabled',
        validation: []
      },
      {
        property: 'showUsageInCustomerPortal',
        validation: []
      },
      {
        property: 'showAppUrl',
        validation: []
      },
      {
        property: 'appUrl',
        validation: [isUrl]
      },
      {
        property: 'customerPortalAuthRequirement',
        validation: []
      }
    ]
  })

  type Mutate = <T>(
    field:
      | 'customerPortalEnabled'
      | 'showUsageInCustomerPortal'
      | 'appUrl'
      | 'customerPortalAuthRequirement',
    opts: { label: string; value: T }
  ) => void

  const mutate: Mutate = (field, { label, value }) => {
    if (
      !data ||
      value === data?.[field] ||
      fields[field].validationErrors.length > 0
    ) {
      return
    }

    mutator.mutate(
      {
        id: data?.id,
        body: {
          bankAccountDetails: data.bankAccountDetails,
          legalCompanyName: data.legalCompanyName,
          address: data.address,
          email: data.email,
          customerPortalEnabled: data.customerPortalEnabled,
          showUsageInCustomerPortal: data.showUsageInCustomerPortal,
          appUrl: data.appUrl,
          customerPortalAuthRequirement: data.customerPortalAuthRequirement,
          [field]: value
        }
      },
      {
        onSuccess: () => {
          displayNotification(`${label} updated`, {
            type: 'success'
          })
        },
        onError: () => {
          displayNotification(`${label} update failed.`, {
            type: 'error'
          })
        }
      }
    )
  }

  return {
    queries,
    fields,
    isPending: mutator.isPending,
    mutate
  }
}
